import type { ReactNode } from 'react';

import { Box } from '@mui/material';

import Typography from '@/shared/ui/typography/Text';

export interface DrawerHeaderBarProps {
  /** Icon shown inline before the title (e.g. 32px feature icon). */
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Left slot rendered before the icon/title (e.g. a back button). */
  before?: ReactNode;
  /** Inline action aligned to the right of the title group (e.g. "New chat"). */
  headerAction?: ReactNode;
  /** Right-side action buttons (e.g. history / close / discard). */
  actions?: ReactNode;
}

/**
 * Shared chrome header for side drawers: an optional left slot, an icon + 24px
 * bold title (with optional subtitle), an optional inline action, and an
 * optional right-side action slot. The single source of truth for drawer header
 * structure and typography — used by both `SideDrawer` and the dashboard drawer.
 */
export const DrawerHeaderBar = ({
  icon,
  title,
  subtitle,
  before,
  headerAction,
  actions
}: DrawerHeaderBarProps) => (
  <Box
    sx={{
      width: '100%',
      minHeight: '108px',
      display: 'flex',
      justifyContent: 'space-between',
      gap: '16px',
      alignItems: 'stretch',
      borderBottom: '1px solid',
      borderBottomColor: 'gx.textDisabled'
    }}
  >
    {before}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: 1,
        pl: before ? 0 : '24px',
        pr: '32px'
      }}
    >
      <Box>
        <Typography
          component='div'
          sx={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            fontSize: '24px',
            fontWeight: 'bold',
            height: '60px'
          }}
        >
          {icon}
          {title}
        </Typography>
        {subtitle && (
          <Typography
            component='div'
            sx={{
              color: 'gx.textDisabled',
              fontFamily: 'Cairo',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: 'normal'
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {headerAction}
    </Box>
    {actions != null && <Box sx={{ display: 'flex', gap: '2px' }}>{actions}</Box>}
  </Box>
);

export default DrawerHeaderBar;
